import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Dashboard as BoardIcon,
  SportsFootball as GameIcon,
  Payments as PayoutIcon,
  AccountBalanceWallet as AccountingIcon,
  People as PlayersIcon,
  Toll as CreditsIcon,
  Palette as ThemesIcon,
} from '@mui/icons-material';
import { SupportLayout } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';

interface GlossaryTerm {
  term: string;
  definition: string;
  category: string;
  relatedTerms?: string[];
}

const glossaryTerms: GlossaryTerm[] = [
  // Board Terms
  {
    term: 'Board',
    definition: 'A 10x10 grid where players claim squares to participate in a squares pool. Each board is associated with a specific game or event.',
    category: 'Boards',
    relatedTerms: ['Square', 'Grid'],
  },
  {
    term: 'Square',
    definition: 'One of the 100 cells in a board\'s 10x10 grid. Each square represents a potential winning combination based on the last digit of each team\'s score.',
    category: 'Boards',
    relatedTerms: ['Board', 'Claim'],
  },
  {
    term: 'Grid',
    definition: 'The 10x10 arrangement of squares that makes up a board. The grid has rows (0-9) and columns (0-9), each representing a score\'s last digit.',
    category: 'Boards',
    relatedTerms: ['Board', 'Square'],
  },
  {
    term: 'Draft',
    definition: 'A board status indicating the board is still being set up and is not yet open for players to join. Changes can be freely made during this phase.',
    category: 'Boards',
    relatedTerms: ['Live', 'Locked', 'Publish'],
  },
  {
    term: 'Live',
    definition: 'A board status indicating the board is published and open for players to claim squares. The game hasn\'t started yet.',
    category: 'Boards',
    relatedTerms: ['Draft', 'Locked', 'Publish'],
  },
  {
    term: 'Locked',
    definition: 'A board status indicating no more square claims are allowed. This typically happens when the game starts or all scores are recorded. Numbers must be randomized before locking.',
    category: 'Boards',
    relatedTerms: ['Live', 'Completed'],
  },
  {
    term: 'Completed',
    definition: 'A board status indicating all quarters/periods have been scored and the game is finished. Final payouts are determined.',
    category: 'Boards',
    relatedTerms: ['Locked', 'Hit'],
  },
  {
    term: 'Publish',
    definition: 'The action of making a board visible to players. Publishing moves a board from draft to live status.',
    category: 'Boards',
    relatedTerms: ['Draft', 'Live'],
  },
  {
    term: 'Invite Code',
    definition: 'A unique 6-character code that players use to join a specific board. Share this code with friends to let them participate.',
    category: 'Boards',
    relatedTerms: ['Board', 'Claim'],
  },
  {
    term: 'Slug',
    definition: 'A human-readable URL identifier for a board (e.g., "super-bowl-2024"). Used in shareable links.',
    category: 'Boards',
  },

  // Game Terms
  {
    term: 'Claim',
    definition: 'The action of selecting and taking ownership of a square on a board. Once claimed, a square belongs to that player.',
    category: 'Game',
    relatedTerms: ['Square', 'Assign'],
  },
  {
    term: 'Randomize',
    definition: 'The process of randomly assigning the digits 0-9 to rows and columns. This ensures fair play as no one can predict which numbers they\'ll get.',
    category: 'Game',
    relatedTerms: ['Numbers', 'Lock'],
  },
  {
    term: 'Numbers',
    definition: 'The digits 0-9 assigned to each row and column after randomization. These determine which square wins based on game scores.',
    category: 'Game',
    relatedTerms: ['Randomize', 'Last Digit'],
  },
  {
    term: 'Last Digit',
    definition: 'The final digit of a team\'s score (e.g., if the score is 24, the last digit is 4). Used to find the winning square.',
    category: 'Game',
    relatedTerms: ['Winner', 'Hit'],
  },
  {
    term: 'Hit',
    definition: 'A recorded score for a specific quarter or period. Each hit determines a winner for that scoring period.',
    category: 'Game',
    relatedTerms: ['Winner', 'Quarter'],
  },
  {
    term: 'Winner',
    definition: 'The player who owns the square at the intersection of the two teams\' score last digits. Winners receive payouts.',
    category: 'Game',
    relatedTerms: ['Hit', 'Payout'],
  },
  {
    term: 'Quarter',
    definition: 'A scoring period in a game (Q1, Q2, Q3, Q4). Winners are determined at the end of each quarter based on the score.',
    category: 'Game',
    relatedTerms: ['Period', 'Hit'],
  },
  {
    term: 'Period',
    definition: 'A scoring interval in sports like hockey (P1, P2, P3) or halves in soccer (1H, 2H). Similar to quarters but for different sports.',
    category: 'Game',
    relatedTerms: ['Quarter', 'Half'],
  },
  {
    term: 'Half',
    definition: 'A scoring period representing half of the game (1H, 2H). Used in soccer and some board configurations.',
    category: 'Game',
    relatedTerms: ['Period', 'Quarter'],
  },
  {
    term: 'Number Mode',
    definition: 'The configuration determining how many sets of random numbers a board uses. Single mode uses one set for all quarters; half mode uses two sets; quarter mode uses four sets.',
    category: 'Game',
    relatedTerms: ['Randomize', 'Numbers'],
  },

  // Payout Terms
  {
    term: 'Pot',
    definition: 'The total amount of money collected from all square purchases. This is distributed to winners as payouts.',
    category: 'Payouts',
    relatedTerms: ['Payout', 'Price per Square'],
  },
  {
    term: 'Payout',
    definition: 'The money awarded to a winner for a specific quarter. The payout amount depends on the pot size and payout configuration.',
    category: 'Payouts',
    relatedTerms: ['Pot', 'Winner'],
  },
  {
    term: 'Price per Square',
    definition: 'The cost to claim one square on the board. All 100 squares at this price equals the total pot.',
    category: 'Payouts',
    relatedTerms: ['Pot', 'Square'],
  },
  {
    term: 'Right Way',
    definition: 'The standard payout based on the actual score. The intersection of the home team\'s last digit (columns) and away team\'s last digit (rows) determines the winner.',
    category: 'Payouts',
    relatedTerms: ['Wrong Way', 'Reverse'],
  },
  {
    term: 'Wrong Way',
    definition: 'Also called "Reverse." A secondary payout where the team digits are swapped - the away team\'s digit is used for columns and home team\'s for rows.',
    category: 'Payouts',
    relatedTerms: ['Right Way', 'Reverse'],
  },
  {
    term: 'Reverse',
    definition: 'Another name for Wrong Way payout. Uses swapped team digits to find a secondary winner.',
    category: 'Payouts',
    relatedTerms: ['Wrong Way', 'Right Way'],
  },
  {
    term: '+5 Rule',
    definition: 'An optional payout rule that adds 5 (or another number) to one team\'s last digit to find an additional winner. Creates more winning opportunities.',
    category: 'Payouts',
    relatedTerms: ['Payout', 'Winner'],
  },
  {
    term: 'Neighbors',
    definition: 'An optional payout where squares adjacent to the winning square also receive a smaller payout. Creates more winners per quarter.',
    category: 'Payouts',
    relatedTerms: ['Winner', 'Payout'],
  },
  {
    term: 'Organizer Profit',
    definition: 'An optional percentage (0-50%) that the board organizer keeps from the pot before distributing payouts to winners.',
    category: 'Payouts',
    relatedTerms: ['Pot', 'Payout'],
  },
  {
    term: 'Bad Numbers',
    definition: 'A compensation feature for players with "unlucky" number combinations. Squares where BOTH the row AND column are designated bad digits (commonly 2, 5, 8, 9 in football) receive an automatic fixed payout when the board is locked, before the game starts.',
    category: 'Payouts',
    relatedTerms: ['Payout', 'Locked'],
  },
  {
    term: 'Overtime',
    definition: 'When enabled, the final quarter payout is split between end of regulation and final score if the game goes to overtime.',
    category: 'Payouts',
    relatedTerms: ['Quarter', 'Payout'],
  },

  // Accounting Terms
  {
    term: 'Ledger',
    definition: 'A record of all financial transactions for a board, including square purchases, winnings, and payments.',
    category: 'Accounting',
    relatedTerms: ['Balance', 'Transaction'],
  },
  {
    term: 'Balance',
    definition: 'The net amount a player owes or is owed. Negative balance means they owe money; positive means they\'re owed money (usually from winnings).',
    category: 'Accounting',
    relatedTerms: ['Ledger', 'Payment'],
  },
  {
    term: 'Payment',
    definition: 'A recorded transaction when a player pays what they owe or receives their winnings.',
    category: 'Accounting',
    relatedTerms: ['Balance', 'Ledger'],
  },
  {
    term: 'Outstanding',
    definition: 'Money still owed by players who haven\'t paid for their claimed squares.',
    category: 'Accounting',
    relatedTerms: ['Balance', 'Owed'],
  },
  {
    term: 'Winnings',
    definition: 'Money earned by a player from owning a winning square. Tracked in the ledger and can be marked as paid.',
    category: 'Accounting',
    relatedTerms: ['Winner', 'Payout', 'Balance'],
  },

  // Player Terms
  {
    term: 'Manager',
    definition: 'A user with administrative access to a board. Can assign squares, record scores, and manage settings. Board owners are automatically managers.',
    category: 'Players',
    relatedTerms: ['Owner', 'Participant'],
  },
  {
    term: 'Owner',
    definition: 'The user who created the board. Has full control including the ability to delete the board and manage other managers.',
    category: 'Players',
    relatedTerms: ['Manager'],
  },
  {
    term: 'Participant',
    definition: 'Any player who has claimed or been assigned at least one square on a board.',
    category: 'Players',
    relatedTerms: ['Player', 'Claim'],
  },
  {
    term: 'Offline Player',
    definition: 'A placeholder for someone who doesn\'t have a SquareUp account. The manager handles their squares and payments on their behalf.',
    category: 'Players',
    relatedTerms: ['Participant', 'Assign'],
  },
  {
    term: 'Assign',
    definition: 'The action of a manager giving a square to a specific player or offline player, rather than letting them claim it themselves.',
    category: 'Players',
    relatedTerms: ['Claim', 'Manager'],
  },
  {
    term: 'Handle',
    definition: 'A 4-character identifier that appears on squares you own. Makes it easy to find your squares on the grid.',
    category: 'Players',
    relatedTerms: ['Display Name'],
  },
  {
    term: 'Display Name',
    definition: 'Your visible name shown to other players on boards you participate in.',
    category: 'Players',
    relatedTerms: ['Handle'],
  },

  // Credits Terms
  {
    term: 'Credit',
    definition: 'The currency used to create boards in SquareUp. One credit = one board. Credits never expire.',
    category: 'Credits',
    relatedTerms: ['Board', 'Credit Pack'],
  },
  {
    term: 'Credit Pack',
    definition: 'A bundle of credits available for purchase. Larger packs offer better per-board pricing.',
    category: 'Credits',
    relatedTerms: ['Credit'],
  },
  {
    term: 'Referral',
    definition: 'Sharing SquareUp with friends using your unique link. When they sign up and purchase credits, you both earn bonus credits.',
    category: 'Credits',
    relatedTerms: ['Credit'],
  },

  // Theme Terms
  {
    term: 'Theme',
    definition: 'A visual style applied to a board, including colors, backgrounds, and styling. Themes can be purchased from the Theme Store.',
    category: 'Themes',
    relatedTerms: ['Board'],
  },
  {
    term: 'Theme Store',
    definition: 'The marketplace where you can browse and purchase themes to customize your boards.',
    category: 'Themes',
    relatedTerms: ['Theme'],
  },
];

const categoryIcons: Record<string, React.ReactNode> = {
  Boards: <BoardIcon />,
  Game: <GameIcon />,
  Payouts: <PayoutIcon />,
  Accounting: <AccountingIcon />,
  Players: <PlayersIcon />,
  Credits: <CreditsIcon />,
  Themes: <ThemesIcon />,
};

const categoryColors: Record<string, string> = {
  Boards: '#2196f3',
  Game: '#9c27b0',
  Payouts: '#ff9800',
  Accounting: '#f44336',
  Players: '#4caf50',
  Credits: '#00bcd4',
  Themes: '#e91e63',
};

export const GlossaryPage: React.FC = () => {
  useDocumentTitle('Glossary - Support');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = [...new Set(glossaryTerms.map((term) => term.category))];

  const filteredTerms = glossaryTerms.filter((term) => {
    const matchesSearch = searchQuery
      ? term.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
        term.definition.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    const matchesCategory = selectedCategory ? term.category === selectedCategory : true;
    return matchesSearch && matchesCategory;
  });

  // Sort alphabetically by term
  const sortedTerms = [...filteredTerms].sort((a, b) => a.term.localeCompare(b.term));

  // Group by first letter
  const groupedTerms = sortedTerms.reduce((acc, term) => {
    const letter = term.term[0].toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(term);
    return acc;
  }, {} as Record<string, GlossaryTerm[]>);

  const letters = Object.keys(groupedTerms).sort();

  return (
    <SupportLayout
      title="Glossary"
      description="Definitions of common terms used in SquareUp and sports squares pools."
      breadcrumbs={[{ label: 'Glossary' }]}
    >
      {/* Search and Filter */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, bgcolor: 'grey.50' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              placeholder="Search terms..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: 'white' }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Filter:
              </Typography>
              <Chip
                label="All"
                variant={selectedCategory === null ? 'filled' : 'outlined'}
                color={selectedCategory === null ? 'primary' : 'default'}
                onClick={() => setSelectedCategory(null)}
                size="small"
              />
              {categories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  variant={selectedCategory === category ? 'filled' : 'outlined'}
                  onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                  size="small"
                  icon={categoryIcons[category] as React.ReactElement}
                  sx={{
                    ...(selectedCategory === category && {
                      bgcolor: categoryColors[category],
                      color: 'white',
                      '& .MuiChip-icon': { color: 'white' },
                    }),
                  }}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
        {searchQuery && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Found {filteredTerms.length} term{filteredTerms.length !== 1 ? 's' : ''} for "{searchQuery}"
          </Typography>
        )}
      </Paper>

      {/* Alphabet Navigation */}
      <Box sx={{ mb: 3, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {letters.map((letter) => (
          <Chip
            key={letter}
            label={letter}
            size="small"
            variant="outlined"
            onClick={() => {
              document.getElementById(`letter-${letter}`)?.scrollIntoView({ behavior: 'smooth' });
            }}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>

      {/* Terms List */}
      {filteredTerms.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: 1, borderColor: 'divider' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No terms found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try different keywords or clear the filter
          </Typography>
        </Paper>
      ) : (
        <Box>
          {letters.map((letter) => (
            <Box key={letter} id={`letter-${letter}`} sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                color="primary"
                sx={{ mb: 2, borderBottom: 2, borderColor: 'primary.main', pb: 1 }}
              >
                {letter}
              </Typography>
              <Grid container spacing={2}>
                {groupedTerms[letter].map((term) => (
                  <Grid size={{ xs: 12, md: 6 }} key={term.term}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: 'divider',
                        height: '100%',
                        '&:hover': { borderColor: categoryColors[term.category], bgcolor: 'grey.50' },
                        transition: 'all 0.2s',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {term.term}
                        </Typography>
                        <Chip
                          label={term.category}
                          size="small"
                          icon={categoryIcons[term.category] as React.ReactElement}
                          sx={{
                            bgcolor: `${categoryColors[term.category]}20`,
                            color: categoryColors[term.category],
                            '& .MuiChip-icon': { color: categoryColors[term.category] },
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {term.definition}
                      </Typography>
                      {term.relatedTerms && term.relatedTerms.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            Related:
                          </Typography>
                          {term.relatedTerms.map((related) => (
                            <Chip
                              key={related}
                              label={related}
                              size="small"
                              variant="outlined"
                              onClick={() => setSearchQuery(related)}
                              sx={{ height: 20, fontSize: '0.7rem', cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      )}

      {/* Category Legend */}
      <Divider sx={{ my: 4 }} />
      <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Categories
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {categories.map((category) => (
            <Box key={category} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ color: categoryColors[category] }}>{categoryIcons[category]}</Box>
              <Typography variant="body2">{category}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    </SupportLayout>
  );
};

export default GlossaryPage;
